import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";

const FieldLabel = ({ children, muted }) => (
  <div style={{
    color: "#6b7280", fontSize: 11, fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8,
  }}>
    {children}
    {muted && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, fontSize: 11, color: "#9ca3af" }}>{muted}</span>}
  </div>
);

const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 16px" }}>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
    <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase" }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const readOnlyStyle = {
  ...inputStyle,
  backgroundColor: "#f9fafb",
  color: "#6b7280",
  cursor: "not-allowed",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const renderValue = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
};

const EditTagAssignment = ({
  show,
  handleClose,
  handleFormSubmit,
  orderAssignmentId,
  orderData,
  assignment,
  isBrandAdmin,
  clientId,
}) => {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [minSerial, setMinSerial] = useState("");
  const [maxSerial, setMaxSerial] = useState("");
  const [metadataFields, setMetadataFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [userEmail] = useState("adrenreuel@gmail.com");

  useEffect(() => {
    if (!show) return;

    const currentItemId =
      assignment?.itemId ||
      assignment?.item?._id ||
      assignment?._id ||
      "selected-item";
    const currentItemCode = assignment?.itemCode || "Selected item";

    setSelectedItemId(currentItemId);

    setMinSerial(
      Number.isFinite(assignment?.minSerial) ? assignment.minSerial : ""
    );
    setMaxSerial(
      Number.isFinite(assignment?.maxSerial) ? assignment.maxSerial : ""
    );

    const mf = Object.entries(assignment?.metadata || {}).map(
      ([key, value]) => ({ key, value })
    );
    setMetadataFields(mf);

    const fetchItems = async () => {
      try {
        const url =
          isBrandAdmin && clientId
            ? `https://api.iriscrypt.com/item/by-client/${encodeURIComponent(clientId)}`
            : "https://api.iriscrypt.com/item";
        const res = await fetch(url, {
          headers: { "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43" },
        });
        const result = await res.json();
        setItems(result || []);
      } catch {
        setItems([{ _id: currentItemId, code: currentItemCode }]);
      }
    };
    fetchItems();
  }, [show, assignment, isBrandAdmin, clientId]);

  useEffect(() => {
    if (!show) {
      setItems([]);
      setSelectedItemId("");
      setMinSerial("");
      setMaxSerial("");
      setMetadataFields([]);
      setSaving(false);
    }
  }, [show]);

  const addMetadataField = () => {
    setMetadataFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeMetadataField = (index) => {
    setMetadataFields((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMetadataField = (index, keyOrValue, newValue) => {
    setMetadataFields((prev) => {
      const copy = [...prev];
      copy[index][keyOrValue] = newValue;
      return copy;
    });
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    setMetadataFields((prev) => {
      const copy = [...prev];
      [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
      return copy;
    });
  };

  const moveFieldDown = (index) => {
    setMetadataFields((prev) => {
      if (index === prev.length - 1) return prev;
      const copy = [...prev];
      [copy[index], copy[index + 1]] = [copy[index + 1], copy[index]];
      return copy;
    });
  };

  const handleSubmit = async () => {
    if (!assignment?._id) { alert("Missing assignment id."); return; }

    const metadata = {};
    for (const field of metadataFields) {
      if (field.key) metadata[field.key] = field.value ?? "";
    }

    const payload = {
      metadata,
      user: userEmail,
      ...(isBrandAdmin && clientId ? { client: clientId } : {}),
    };

    setSaving(true);
    try {
      const res = await fetch(
        `https://api.iriscrypt.com/tagAssignment/${encodeURIComponent(assignment._id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : {}; }
      catch { data = { message: text }; }

      if (!res.ok) {
        const msg =
          data?.message || data?.error || data?.detail?.[0]?.msg ||
          `Update failed with status ${res.status}`;
        console.error("Edit tag assignment failed:", { status: res.status, payload, server: data });
        alert(msg);
        return;
      }

      if (data?.success === false) {
        alert(data?.message || "Failed to update assignment.");
        return;
      }

      handleClose();
      handleFormSubmit();
    } catch (err) {
      console.error("Assignment update error:", err);
      alert("An unexpected error occurred while updating the assignment.");
    } finally {
      setSaving(false);
    }
  };

  const selectedItem = items.find((i) => i._id === selectedItemId);

  return (
    <>
      <style>{`
        .edit-tag-assignment-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .edit-tag-assignment-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .edit-tag-assignment-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .edit-tag-assignment-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .add-meta-tag-btn:hover {
          border-color: #ff5100 !important;
          color: #ff5100 !important;
          background: rgba(255,81,0,0.04) !important;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="edit-tag-assignment-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: "rgba(255,81,0,0.08)",
              border: "1.5px solid rgba(255,81,0,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fas fa-tag" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Edit Assignment</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>Update assignment metadata</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Item — read only preview */}
          <div>
            <FieldLabel muted="read only">Product SKU</FieldLabel>
            {selectedItem ? (
              <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
                <div
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", background: "#f9fafb", borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Selected SKU
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", marginTop: 2 }}>
                      {selectedItem.code}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "4px 10px", borderRadius: 999,
                      background: "rgba(255,81,0,0.08)", border: "1px solid rgba(255,81,0,0.2)",
                      fontSize: 11, fontWeight: 700, color: "#ff5100",
                    }}
                  >
                    <i className="fas fa-lock" style={{ fontSize: 10 }} />
                    Read only
                  </div>
                </div>

                <div style={{ padding: "10px 14px" }}>
                  {selectedItem.metadata && Object.keys(selectedItem.metadata).length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {Object.entries(selectedItem.metadata).map(([k, v]) => (
                        <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                          <span style={{ color: "#6b7280", fontWeight: 600, textTransform: "uppercase", fontSize: 11, letterSpacing: "0.04em" }}>
                            {k}
                          </span>
                          <span style={{ color: "#111827", fontWeight: 500 }}>
                            {renderValue(v) || <span style={{ color: "#d1d5db" }}>—</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "#9ca3af" }}>No metadata for this SKU.</div>
                  )}

                  {(selectedItem.timestamp || selectedItem.updated_at) && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6", display: "flex", gap: 16, fontSize: 11, color: "#9ca3af" }}>
                      {selectedItem.timestamp && (
                        <span>Added {new Date(selectedItem.timestamp).toLocaleDateString()}</span>
                      )}
                      {selectedItem.updated_at && (
                        <span>Updated {new Date(selectedItem.updated_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <input
                type="text"
                value={assignment?.itemCode || ""}
                readOnly
                style={readOnlyStyle}
              />
            )}
          </div>

          {/* Serials — read only, side by side */}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel muted="read only">Min Serial</FieldLabel>
              <input
                type="text"
                value={minSerial}
                readOnly
                style={readOnlyStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel muted="read only">Max Serial</FieldLabel>
              <input
                type="text"
                value={maxSerial}
                readOnly
                style={readOnlyStyle}
              />
            </div>
          </div>

          {/* Metadata */}
          {metadataFields.length > 0 && (
            <>
              <SectionDivider label="Metadata" />
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {metadataFields.map((field, index) => (
                  <div key={index} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => updateMetadataField(index, "key", e.target.value)}
                      placeholder="Key"
                      disabled={saving}
                      style={{ ...inputStyle, flex: 1, opacity: saving ? 0.6 : 1 }}
                      {...focusHandlers}
                    />
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => updateMetadataField(index, "value", e.target.value)}
                      placeholder="Value"
                      disabled={saving}
                      style={{ ...inputStyle, flex: 1, opacity: saving ? 0.6 : 1 }}
                      {...focusHandlers}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => moveFieldUp(index)}
                        disabled={index === 0 || saving}
                        style={{
                          width: 28, height: 28, borderRadius: 6, border: "1.5px solid #e5e7eb",
                          background: "#fff", cursor: (index === 0 || saving) ? "not-allowed" : "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          opacity: index === 0 ? 0.4 : 1, flexShrink: 0,
                        }}
                      >
                        <i className="fas fa-chevron-up" style={{ fontSize: 10, color: "#6b7280" }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveFieldDown(index)}
                        disabled={index === metadataFields.length - 1 || saving}
                        style={{
                          width: 28, height: 28, borderRadius: 6, border: "1.5px solid #e5e7eb",
                          background: "#fff", cursor: (index === metadataFields.length - 1 || saving) ? "not-allowed" : "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          opacity: index === metadataFields.length - 1 ? 0.4 : 1, flexShrink: 0,
                        }}
                      >
                        <i className="fas fa-chevron-down" style={{ fontSize: 10, color: "#6b7280" }} />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMetadataField(index)}
                      disabled={saving}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: "1.5px solid #fca5a5",
                        background: "#fef2f2", color: "#ef4444", cursor: saving ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}
                    >
                      <i className="fas fa-times" style={{ fontSize: 12 }} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Add metadata button */}
          <button
            type="button"
            onClick={addMetadataField}
            disabled={saving}
            className="add-meta-tag-btn"
            style={{
              marginTop: metadataFields.length > 0 ? 10 : 20,
              width: "100%", padding: "9px 0", borderRadius: 10,
              border: "1.5px dashed #d1d5db", background: "transparent",
              color: "#9ca3af", fontSize: 13, fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 7, transition: "border-color 0.15s, color 0.15s, background 0.15s",
              opacity: saving ? 0.6 : 1,
            }}
          >
            <i className="fas fa-plus" style={{ fontSize: 11 }} />
            Add variable batch-wise metadata
          </button>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            style={{
              width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
              background: saving ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: saving ? "#9ca3af" : "#fff",
              fontWeight: 700, fontSize: 15,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 9, transition: "background 0.15s",
            }}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                Saving…
              </>
            ) : (
              <>
                Save Changes
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditTagAssignment;
